import React, { useState } from 'react';
import { collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { parseICS, groupLessonsByStudent } from '../../utils/icsParser';
import './ImportLessons.css';

function ImportLessons({ onImportComplete }) {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [importStats, setImportStats] = useState(null);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setImportStats(null);

    // Read and parse file for preview
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const icsContent = event.target.result;
        const lessons = parseICS(icsContent);

        console.log('Parsed lessons:', lessons.length);
        console.log('First 5 lessons:', lessons.slice(0, 5));
        console.log('Sample lesson:', lessons[0]);

        // Generate preview stats
        const studentGroups = groupLessonsByStudent(lessons);
        const studentCount = Object.keys(studentGroups).length;
        const locationCounts = {};

        lessons.forEach(lesson => {
          locationCounts[lesson.location] = (locationCounts[lesson.location] || 0) + 1;
        });

        const previewData = {
          totalLessons: lessons.length,
          studentCount,
          students: Object.keys(studentGroups).sort(),
          locationCounts,
          dateRange: getDateRange(lessons),
          sampleLessons: lessons.slice(0, 10)
        };

        console.log('Preview data:', previewData);
        console.log('Sample lessons for table:', previewData.sampleLessons);

        setPreview(previewData);
      } catch (error) {
        console.error('Error parsing ICS file:', error);
        alert('Error parsing ICS file. Please check the file format.');
      }
    };

    reader.readAsText(selectedFile);
  };

  const getDateRange = (lessons) => {
    if (lessons.length === 0) return { start: null, end: null };

    const dates = lessons.map(l => l.date).sort();
    return {
      start: dates[0],
      end: dates[dates.length - 1]
    };
  };

  const handleImport = async () => {
    if (!file || !preview) return;

    if (!window.confirm(
      `Import ${preview.totalLessons} lessons for ${preview.studentCount} students?\n\n` +
      `This will check for duplicates before importing.`
    )) {
      return;
    }

    setImporting(true);

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const icsContent = event.target.result;
          const lessons = parseICS(icsContent);

          // Import lessons with duplicate checking
          const stats = await importLessonsToFirestore(lessons);

          setImportStats(stats);
          alert(
            `Import Complete!\n\n` +
            `Imported: ${stats.imported}\n` +
            `Skipped (duplicates): ${stats.skipped}\n` +
            `Errors: ${stats.errors}`
          );

          if (onImportComplete) {
            onImportComplete();
          }
        } catch (error) {
          console.error('Error importing lessons:', error);
          alert('Error importing lessons: ' + error.message);
        } finally {
          setImporting(false);
        }
      };

      reader.readAsText(file);
    } catch (error) {
      console.error('Error reading file:', error);
      alert('Error reading file: ' + error.message);
      setImporting(false);
    }
  };

  const importLessonsToFirestore = async (lessons) => {
    const stats = {
      imported: 0,
      skipped: 0,
      errors: 0
    };

    for (const lesson of lessons) {
      try {
        // Check for duplicate by date, time, and student name
        const lessonsRef = collection(db, 'lessons');
        const q = query(
          lessonsRef,
          where('date', '==', lesson.date),
          where('time', '==', lesson.time),
          where('studentName', '==', lesson.studentName)
        );

        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          // Duplicate found, skip
          stats.skipped++;
          continue;
        }

        // Get student ID from students collection
        const studentsRef = collection(db, 'students');
        const studentQuery = query(
          studentsRef,
          where('name', '==', lesson.studentName)
        );
        const studentSnapshot = await getDocs(studentQuery);

        let studentId = null;
        if (!studentSnapshot.empty) {
          studentId = studentSnapshot.docs[0].id;
        }

        // Add lesson to Firestore
        await addDoc(collection(db, 'lessons'), {
          studentName: lesson.studentName,
          studentId: studentId,
          date: lesson.date,
          time: lesson.time,
          location: lesson.location,
          importedFrom: 'MMS',
          importedAt: new Date().toISOString(),
          mmsUID: lesson.mmsUID,
          createdAt: new Date()
        });

        stats.imported++;
      } catch (error) {
        console.error('Error importing lesson:', error, lesson);
        stats.errors++;
      }
    }

    return stats;
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setImportStats(null);
  };

  return (
    <div className="import-lessons-container">
      <h2>Import Lessons from ICS Calendar</h2>

      {!file && (
        <div className="file-upload-section">
          <p>Select an ICS calendar file exported from MyMusicStaff or Google Calendar.</p>
          <input
            type="file"
            accept=".ics"
            onChange={handleFileSelect}
            className="file-input"
          />
        </div>
      )}

      {preview && !importStats && (
        <div className="preview-section">
          <h3>Preview Import</h3>

          <div className="preview-stats">
            <div className="stat-card">
              <div className="stat-number">{preview.totalLessons}</div>
              <div className="stat-label">Total Lessons</div>
            </div>

            <div className="stat-card">
              <div className="stat-number">{preview.studentCount}</div>
              <div className="stat-label">Students</div>
            </div>

            <div className="stat-card">
              <div className="stat-number">
                {preview.locationCounts.Lakeville || 0}
              </div>
              <div className="stat-label">Lakeville</div>
            </div>

            <div className="stat-card">
              <div className="stat-number">
                {preview.locationCounts.Headline || 0}
              </div>
              <div className="stat-label">Headline</div>
            </div>
          </div>

          <div className="date-range">
            <strong>Date Range:</strong> {preview.dateRange.start} to {preview.dateRange.end}
          </div>

          <div className="students-list">
            <strong>Students ({preview.studentCount}):</strong>
            <div className="student-chips">
              {preview.students.map(student => (
                <span key={student} className="student-chip">{student}</span>
              ))}
            </div>
          </div>

          <div className="sample-lessons">
            <strong>Sample Lessons (first 10):</strong>
            <table className="lessons-preview-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                {preview.sampleLessons.map((lesson, index) => {
                  console.log('Rendering lesson row:', index, lesson);
                  return (
                    <tr key={index}>
                      <td>{lesson?.studentName || 'N/A'}</td>
                      <td>{lesson?.date || 'N/A'}</td>
                      <td>{lesson?.time || 'N/A'}</td>
                      <td>{lesson?.location || 'N/A'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="button-group">
            <button
              onClick={handleImport}
              disabled={importing}
              className="btn btn-primary"
            >
              {importing ? 'Importing...' : `Import ${preview.totalLessons} Lessons`}
            </button>
            <button
              onClick={handleReset}
              disabled={importing}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {importStats && (
        <div className="import-results">
          <h3>Import Complete!</h3>

          <div className="results-stats">
            <div className="result-card success">
              <div className="result-number">{importStats.imported}</div>
              <div className="result-label">Imported</div>
            </div>

            <div className="result-card skipped">
              <div className="result-number">{importStats.skipped}</div>
              <div className="result-label">Skipped (Duplicates)</div>
            </div>

            {importStats.errors > 0 && (
              <div className="result-card error">
                <div className="result-number">{importStats.errors}</div>
                <div className="result-label">Errors</div>
              </div>
            )}
          </div>

          <button onClick={handleReset} className="btn btn-primary">
            Import Another File
          </button>
        </div>
      )}
    </div>
  );
}

export default ImportLessons;
